import { useContext, useState } from "react";
import { AuthContext } from "../context/authContext";
import axios from "axios";
import { baseUrl } from "../modules/account/login";
import { useNavigate } from "react-router";
import { keepPreviousData, useQuery } from "@tanstack/react-query";

const url = {
  leads: "leads",
  calls: "calls",
  campaigns: "campaigns",
  knowledgebase: "knowledgebase",
  get_leads_summary: "dashboard/get_leads_summary",
  get_call_stats: "dashboard/get_call_stats",
  get_call_activities: "dashboard/get_call_activities",
  hubspot_data_mapping: "integrations/hubspot/get_data_mapping",
};

type dataType = keyof typeof url;

export default function useFetchData({
  dataType,
  page,
  filter,
  isPost = false,
}: {
  dataType: dataType;
  page?: number;
  filter?: any;
  isPost?: boolean;
}) {
  const { token } = useContext(AuthContext);
  const AuthStr = "Bearer ".concat(token!);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const getData = () => {
    const endpoint = `${baseUrl}/${url[dataType]}`;

    if (isPost) {
      return axios
        .post(endpoint, filter ?? {}, {
          headers: { Authorization: AuthStr },
        })
        .then((response) => {
          setLoading(false);
          return response.data;
        })
        .catch((e) => {
          if (e?.response?.data?.message === "Session expired")
            navigate("/account/logout");
          return Promise.reject(
            new Error(e?.message ?? "Check your internet connection")
          );
        });
    } else {
      const queryParams = new URLSearchParams();

      if (filter) {
        Object.entries(filter).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            queryParams.append(key, String(value));
          }
        });
      }
      if (page !== undefined && (!filter || !("page" in filter))) {
        queryParams.append("page", String(page));
      }

      const fullUrl = `${endpoint}${
        queryParams.toString() ? `?${queryParams.toString()}` : ""
      }`;

      return axios
        .get(fullUrl, {
          headers: { Authorization: AuthStr },
        })
        .then((response) => {
          setLoading(false);
          return response.data;
        })
        .catch((e) => {
          if (e?.response?.data?.message === "Session expired")
            navigate("/account/logout");
          return Promise.reject(
            new Error(
              e?.response?.data?.message ?? "Check your internet connection"
            )
          );
        });
    }
  };

  const { data, error, isLoading, refetch } = useQuery({
    queryKey: [dataType, token, filter],
    queryFn: () => getData(),
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
  });

  return { data, error, isLoading, loading, refetch };
}
